import { Switch, Divider, Stack, Typography } from '@mui/material';
import {
  Notifications,
  GenSnackBar,
  NotificationTypes,
} from '@pinnacleautomotive/clarity-components';
import isEqual from 'lodash.isequal';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  clearSaveStatus,
  openProfileManagerDialog,
  openVerificationModal,
  updateNotificationsPreference,
} from '../../redux/actions/userManagementActions';
import {
  useGetNotificationsQuery,
  useMarkAllNotificationsAsReadMutation,
  useMarkNotificationAsReadMutation,
} from '../../redux/rtk/notifications.services';
import {
  isManager,
  selectPersonId,
  selectSaveSuccessful,
  selectShowConfirmation,
  selectUserNotificationsSettings,
  selectUserPerson,
} from '../../redux/selectors/userDataSelectors';

const SnackBarConfirmation = ({ showSnackBar, saveSuccessful, onClose }) => {
  const { t } = useTranslation('global');
  const snackByStatus = {
    true: {
      type: 'success',
      title: t('notifications.saveSuccessful'),
    },
    false: {
      type: 'error',
      title: t('notifications.noSaveSuccessful'),
    },
  };
  return (
    <GenSnackBar
      title={snackByStatus[saveSuccessful]?.title}
      type={snackByStatus[saveSuccessful]?.type}
      open={showSnackBar}
      onClose={onClose}
    />
  );
};

const NoChannelsSnackBar = ({ showSnackBar, onClose, currentSettings }) => {
  const { t } = useTranslation('global');
  const showNoAdditionalChannelEnalbeSnackBar =
    showSnackBar &&
    !currentSettings.SMS &&
    !currentSettings.SYSTEM &&
    !currentSettings.EMAIL;

  return (
    <GenSnackBar
      title={t('notifications.noChannelsEnabled')}
      color="#323232"
      icon={<></>}
      open={showNoAdditionalChannelEnalbeSnackBar}
      onClose={onClose}
    />
  );
};

export default function AppNotificationCenter() {
  const { t } = useTranslation('global');
  const dispatch = useDispatch();
  const history = useHistory();
  const [newSettings, setNewSettings] = useState(null);
  const currentSettings = useSelector(selectUserNotificationsSettings);
  const personId = useSelector(selectPersonId);
  const person = useSelector(selectUserPerson);
  const saveSuccessful = useSelector(selectSaveSuccessful);
  const showConfirmation = useSelector(selectShowConfirmation);
  const currentUserIsManager = useSelector(isManager);
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching } = useGetNotificationsQuery({
    personId,
    page,
  });
  const [markNotificationAsRead] = useMarkNotificationAsReadMutation();
  const [markAllNotificationsAsRead] = useMarkAllNotificationsAsReadMutation();

  useEffect(() => {
    setNewSettings(currentSettings);
  }, [currentSettings, setNewSettings]);

  const saveNewSettings = () => {
    if (isEqual(newSettings, currentSettings)) return;
    dispatch(updateNotificationsPreference({ newSettings, personId }));
  };

  const dispatchClearSaveStatus = () => {
    dispatch(clearSaveStatus());
  };

  const performAction = ({ notificationId, ...notification }) => {
    if (!notification.readOn)
      markNotificationAsRead({ notificationId, personId });

    let url = `/orders/${notification.payload.vendorOrderId}`;

    if (notification.type === NotificationTypes.ESTIMATE_APPROVAL)
      url = `/approvals`;

    history.push({
      pathname: url,
    });
  };

  const markAllAsRead = () => {
    markAllNotificationsAsRead({ personId });
  };

  const openUpdateProfile = () => {
    dispatch(openProfileManagerDialog());
  };

  const openVerificationDialog = () => {
    dispatch(openVerificationModal());
  };

  return (
    <>
      <SnackBarConfirmation
        onClose={() => dispatchClearSaveStatus()}
        saveSuccessful={saveSuccessful}
        showSnackBar={showConfirmation}
      />
      <NoChannelsSnackBar
        currentSettings={currentSettings}
        onClose={() => dispatchClearSaveStatus()}
        showSnackBar={showConfirmation}
      />
      <Notifications
        color={theme => theme.palette.primary.contrastText}
        notifications={data?.notifications}
        person={person}
        channelsState={newSettings}
        onClose={saveNewSettings}
        onGoBack={saveNewSettings}
        onChannelsStateChange={payload => setNewSettings(payload)}
        onPerformAction={performAction}
        onMarkAllAsRead={markAllAsRead}
        onOpenUpdateProfile={openUpdateProfile}
        onOpenVerificationDialog={openVerificationDialog}
        totalUnreadNotifications={data?.extraValues.totalUnreadNotifications}
        loading={isLoading || isFetching}
        hasNextPage={data?.pagination.pageNumber < data?.pagination.totalPages}
        onLoadMore={() => setPage(page + 1)}
        additionalConfiguration={
          <Stack gap={2}>
            <Typography variant="subtitle2" fontWeight="600">
              {t('notifications.workFlowNotifications')}
            </Typography>
            <Stack gap={2} divider={<Divider />}>
              {!currentUserIsManager && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography color="text.primary">
                    {t('notifications.ordersDeclined')}
                  </Typography>
                  <Switch
                    checked={newSettings?.ORDER_DECLINED}
                    onChange={() =>
                      setNewSettings({
                        ...newSettings,
                        ORDER_DECLINED: !newSettings.ORDER_DECLINED,
                      })
                    }
                  />
                </Stack>
              )}
              {currentUserIsManager && (
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography color="text.primary">
                    {t('notifications.ordersPending')}
                  </Typography>
                  <Switch
                    checked={newSettings?.ORDER_APPROVAL}
                    onChange={() =>
                      setNewSettings({
                        ...newSettings,
                        ORDER_APPROVAL: !newSettings.ORDER_APPROVAL,
                      })
                    }
                  />
                </Stack>
              )}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography color="text.primary">
                  {t('notifications.ordersCompleted')}
                </Typography>
                <Switch
                  checked={newSettings?.ORDER_COMPLETED}
                  onChange={() =>
                    setNewSettings({
                      ...newSettings,
                      ORDER_COMPLETED: !newSettings.ORDER_COMPLETED,
                    })
                  }
                />
              </Stack>
            </Stack>
          </Stack>
        }
      />
    </>
  );
}
